import React, { useState, useEffect, useRef } from 'react'
import { SecondaryBase } from './../Commons/SecondaryBase'
import { useFloatMessage } from './../../Providers/FloatMessage'
import { ResetCurrentDynamic as BLResetCurrentDynamic, SetAutozoneState as BLSetAutozoneState } from './../../../utilities/BL/Zones'
import { ShowWelcomePage as BLShowWelcomePage, SendWelcomePage as BLSendWelcomePage, SetPlannedDynamic as BLSetPlannedDynamic, StartNoisemeter as BLStartNoisemeter, StopNoisemeter as BLStopNoisemeter, ResetNoisemeter as BLResetNoisemeter, GetNoisemeterStats as BLGetNoisemeterStats, GetZonesList as BLGetZonesList } from './../../../utilities/BL/Admin/Overlays/NoisemeterV2'


const NoisemeterV2 = ({ getOutOfTheDynamic }) => {
    const { showMessage, MESSAGE_TYPE } = useFloatMessage();

    // --- CONSTANTES ---
    const STATUS = {
        WAITING: 'Waiting', 
        RUNNING: 'Running',
        FINISHED: 'Finished'
    };

    // --- ESTADOS ---
    const [phase, setPhase] = useState(STATUS.WAITING);
    const [isWorking, setIsWorking] = useState(false);
    const [error, setError] = useState('');

    // Configuración
    const [duration, setDuration] = useState(() => +localStorage.getItem('adm.noisev2.duration') || 30);
    const [sensitivity, setSensitivity] = useState(() => +localStorage.getItem('adm.noisev2.sensitivity') || 1.0);
    const [targetZone, setTargetZone] = useState('all'); 
    
    // Datos Dinámicos
    const [availableZones, setAvailableZones] = useState([]); 
    const [loadingZones, setLoadingZones] = useState(false);
    
    // Estadísticas en Vivo del Ruidómetro
    const [stats, setStats] = useState({
        zones: [],
        activeZone: 'all',
        winner: 'none'
    });

    // Estadísticas en Vivo de Conexiones del Estadio (Tabla tipo Tiro a Gol)
    const [stadiumLiveStats, setStadiumLiveStats] = useState(null);

    // Cronómetro local
    const [elapsedSeconds, setElapsedSeconds] = useState(0);
    const timerIntervalRef = useRef(null);
    const pollingRef = useRef(null);
    const stadiumPollingRef = useRef(null);

    // --- EFECTOS: PERSISTENCIA ---
    useEffect(() => { localStorage.setItem('adm.noisev2.duration', duration); }, [duration]);
    useEffect(() => { localStorage.setItem('adm.noisev2.sensitivity', sensitivity); }, [sensitivity]);

    // --- EFECTO: CARGAR ZONAS AL INICIO Y SEGUIMIENTO DEL ESTADIO ---
    useEffect(() => {
        loadZones();
        
        // Polling constante del Estadio (cada 3 segundos) - Funciona siempre en background
        fetchStadiumLiveStats();
        stadiumPollingRef.current = setInterval(fetchStadiumLiveStats, 3000);

        return () => {
            stopPolling();
            if (stadiumPollingRef.current) clearInterval(stadiumPollingRef.current);
            if (timerIntervalRef.current) clearInterval(timerIntervalRef.current);
        };
    }, []);

    // --- EFECTO: POLLING DEL RUIDÓMETRO ---
    useEffect(() => {
        if (phase === STATUS.RUNNING || phase === STATUS.FINISHED) {
            startPolling();
        } else {
            fetchStatsOnce(); 
            stopPolling();
            setElapsedSeconds(0);
            if (timerIntervalRef.current) {
                clearInterval(timerIntervalRef.current);
                timerIntervalRef.current = null;
            }
        }
    }, [phase]);

    // --- FUNCIONES DEL TEMPORIZADOR LOCAL ---

    const formatTime = (totalSeconds) => {
        const minutes = Math.floor(totalSeconds / 60).toString().padStart(2, '0');
        const seconds = (totalSeconds % 60).toString().padStart(2, '0');
        return `${minutes}:${seconds}`;
    };

    const syncTimer = (startAt, endsAt, serverStatus) => {
        if (serverStatus !== STATUS.RUNNING) {
            setElapsedSeconds(0);
            if (timerIntervalRef.current) {
                clearInterval(timerIntervalRef.current);
                timerIntervalRef.current = null;
            }
            return;
        }

        const startTime = new Date(startAt).getTime();
        const endTime = new Date(endsAt).getTime();
        const totalDuration = Math.round((endTime - startTime) / 1000);

        if (!timerIntervalRef.current) {
            timerIntervalRef.current = setInterval(() => {
                const now = Date.now();
                const elapsed = Math.floor(Math.max(0, now - startTime) / 1000);
                setElapsedSeconds(Math.min(totalDuration, elapsed));
            }, 1000);
        }
    };

    // --- FUNCIONES AUXILIARES DE RED ---

    const loadZones = async () => {
        setLoadingZones(true);
        try {
            const response = await BLGetZonesList(); 
            if (response.success && Array.isArray(response.data)) {
                setAvailableZones(response.data);
                if (targetZone !== 'all' && !response.data.includes(targetZone)) {
                    setTargetZone('all');
                }
            }
        } catch (e) {
            console.error("Error cargando zonas V2:", e);
        } finally {
            setLoadingZones(false);
        }
    };

    // Consultar estadísticas de conexiones de estadio en segundo plano
    const fetchStadiumLiveStats = async () => {
        try {
            const res = await fetch(`${process.env.REACT_APP_API_URL}Stadium/LiveStats`);
            if (res.ok) {
                const data = await res.json();
                setStadiumLiveStats(data);
            }
        } catch (e) {
            console.error("Error consultando LiveStats del estadio:", e);
        }
    };

    const fetchStatsOnce = async () => {
        try {
            const response = await BLGetNoisemeterStats();
            if (response.success && response.data) {
                updateLocalStats(response.data);
            }
        } catch (error) { console.log(error); }
    };

    const startPolling = () => {
        stopPolling();
        fetchStatsOnce(); 
        pollingRef.current = setInterval(fetchStatsOnce, 1000); 
    };

    const stopPolling = () => {
        if (pollingRef.current) clearInterval(pollingRef.current);
    };

    const updateLocalStats = (data) => {
        const serverStatus = typeof data.status === 'number' 
            ? (data.status === 1 ? STATUS.RUNNING : data.status === 2 ? STATUS.FINISHED : STATUS.WAITING)
            : data.status;
        
        if (serverStatus !== phase) {
            setPhase(serverStatus);
        }

        setStats({
            zones: data.zones || [],
            activeZone: data.activeZone || 'all',
            winner: data.winnerZone || 'none'
        });

        if (data.startAt && data.endsAt) {
            syncTimer(data.startAt, data.endsAt, serverStatus);
        }
    };

    // --- HANDLERS (ACCIONES) ---

    const ReturnToDashboard = async () => {
        try {
            await BLResetCurrentDynamic();
            getOutOfTheDynamic();
        } catch (error) {
            getOutOfTheDynamic();
        }
    };

    const ShowPortada = async () => {
        setIsWorking(true);
        setError('');
        const response = await BLShowWelcomePage();
        if (response.success) {
            showMessage({ type: MESSAGE_TYPE.SUCCESS, message: 'Portada del Ruidómetro mostrada en pantallas.' });
        } else {
            setError(response.message);
            showMessage({ type: MESSAGE_TYPE.BAD, message: response.message });
        }
        setIsWorking(false);
    };

    const StartGame = async () => {
        setError('');
        setIsWorking(true);

        const payload = {
            durationSeconds: Math.max(5, parseInt(duration)),
            countdownSeconds: 3,
            sensitivity: parseFloat(sensitivity),
            targetZone: targetZone
        };

        const response = await BLStartNoisemeter(payload);

        if (response.success) {
            setPhase(STATUS.RUNNING);
            showMessage({ type: MESSAGE_TYPE.SUCCESS, message: '¡Ruidómetro iniciado!' });
        } else {
            setError(response.message);
            showMessage({ type: MESSAGE_TYPE.BAD, message: response.message });
        }
        setIsWorking(false);
    };

    const StopGame = async () => {
        setIsWorking(true);
        await BLStopNoisemeter();
        setPhase(STATUS.FINISHED);
        setIsWorking(false);
    };

    const ResetGame = async () => {
        setIsWorking(true);
        await BLResetNoisemeter();
        await BLShowWelcomePage();
        setPhase(STATUS.WAITING);
        setStats({ zones: [], activeZone: 'all', winner: 'none' });
        setElapsedSeconds(0);
        setError('');
        setIsWorking(false);
    };

    const PurgeRoom = async () => {
        if (!window.confirm("¿Estás seguro de iniciar la sala del Ruidómetro? Esto expulsará a todos los dispositivos mandándolos a la pantalla de selección de zona.")) return;
        setIsWorking(true);
        setError('');
        
        try {
            // 1. Desactivamos la asignación automática de zonas (cambiar a modo manual)
            await BLSetAutozoneState(false);

            // Registrar que planeamos jugar Ruidómetro V2 (ID 32) para los celulares que se vayan conectando
            await BLSetPlannedDynamic(32);

            // 2. Limpiamos la dinámica en EntertainmentService
            await BLResetCurrentDynamic(); 

            // 3. Enviamos la señal para mostrar la pantalla de bienvenida con QR en la pantalla gigante
            await BLSendWelcomePage();
 
            // 4. Iniciamos nueva ronda de estadio (StartNewRound) para forzar la purga y el relogin
            const roundRes = await fetch(`${process.env.REACT_APP_API_URL}Stadium/StartNewRound`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ RoundName: 'Ruidómetro V2' })
            });
 
            if (!roundRes.ok) {
                console.error("Error al iniciar nueva ronda de estadio.");
            }
            
            // 5. Reset del Ruidómetro en el backend
            await BLResetNoisemeter();    
            
            // Restablecer estados locales
            setPhase(STATUS.WAITING);
            setStats({ zones: [], activeZone: 'all', winner: 'none' });
            setElapsedSeconds(0);
            
            // Forzar refresco de las estadísticas inmediatamente
            await fetchStadiumLiveStats();

            showMessage({ type: MESSAGE_TYPE.SUCCESS, message: 'Sala iniciada con éxito. Todos los aficionados han sido redirigidos.' });
        } catch (e) {
            setError("Error al iniciar la sala del estadio.");
            showMessage({ type: MESSAGE_TYPE.BAD, message: "Error al purgar sala." });
        } finally {
            setIsWorking(false);
        }
    };

    return (
        <SecondaryBase actionToExecute={ReturnToDashboard}>
            <section className='AdmRunToGoal__Settings'>
                
                <p className='AdmRunToGoal__Title'>
                    <i className="bi bi-speedometer2"></i> Ruidómetro
                </p>

                <div className='AdmRunToGoal__Settings__Controls'>
                    
                    {/* ENCABEZADO: ESTATUS Y CRONÓMETRO */}
                    <div className='AdmRunToGoal__Text--Center border-bottom pb-3 mb-4'>
                        <div className="d-flex justify-content-between align-items-center px-3" style={{gap: '15px'}}>
                            <div>
                                <p className="mb-1 text-muted small" style={{textAlign: 'left'}}>Estatus Servidor</p>
                                <p className="fw-bold mb-0" style={{fontSize: '1.1rem'}}>
                                    {phase === STATUS.WAITING && <><i className="bi bi-hourglass-split"></i> En Espera</>}
                                    {phase === STATUS.RUNNING && <><i className="bi bi-play-circle text-success"></i> ¡MEDICIÓN ACTIVA!</>}
                                    {phase === STATUS.FINISHED && <><i className="bi bi-stop-circle text-danger"></i> Finalizada</>}
                                </p>
                            </div>

                            <div className="bg-dark text-white rounded px-4 py-2 shadow-sm text-center">
                                <p className="mb-0 small text-uppercase" style={{ letterSpacing: '1px', color: '#aaa', fontSize: '0.75rem' }}>Cronómetro</p>
                                <h3 className="mb-0 fw-bold" style={{ fontFamily: 'monospace', fontSize: '1.5rem', marginTop: '2px' }}>
                                    {formatTime(elapsedSeconds)} / {formatTime(duration)}
                                </h3>
                            </div>
                        </div>
                    </div>

                    <div className={phase !== STATUS.WAITING ? 'AdmRunToGoal__DisabledSection' : ''}>
                        
                        <label className='AdmRunToGoal__Settings__Controls__Labels AdmRunToGoal__SpaceUp'>
                            Modo / Zonas Activas:
                        </label>
                        <div style={{display:'flex', gap:'8px', marginBottom: '15px'}}>
                            <select 
                                className='AdmRunToGoal__Settings__Controls__Inputs'
                                value={targetZone}
                                onChange={e => setTargetZone(e.target.value)}
                                disabled={phase !== STATUS.WAITING}
                                style={{flex: 1}}
                            >
                                <option value="all">⚡ Guerra Simultánea (Todos)</option>
                                {availableZones.map((zName) => (
                                    <option key={zName} value={zName}>
                                        Solo {zName.charAt(0).toUpperCase() + zName.slice(1)}
                                    </option>
                                ))}
                            </select>
                            
                            {phase === STATUS.WAITING && (
                                <button 
                                    type="button" 
                                    className="Btn__Adsl--RedOutlineTransparent" 
                                    onClick={loadZones}
                                    style={{width:'auto', padding:'0 12px'}}
                                    title="Actualizar lista de zonas"
                                >
                                    <i className={`bi bi-arrow-repeat ${loadingZones ? 'spin' : ''}`}></i>
                                </button>
                            )}
                        </div>

                        <label className='AdmRunToGoal__Settings__Controls__Labels'>Duración (segundos):</label>
                        <input 
                            className='AdmRunToGoal__Settings__Controls__Inputs' 
                            type='number' 
                            min="5" 
                            value={duration}
                            onChange={e => setDuration(e.target.value)}
                            disabled={phase !== STATUS.WAITING}
                        />

                        <label className='AdmRunToGoal__Settings__Controls__Labels'>Sensibilidad (Multiplicador):</label>
                        <input 
                            className='AdmRunToGoal__Settings__Controls__Inputs' 
                            type='number' 
                            step="0.1" 
                            min="0.1" 
                            max="5.0"
                            value={sensitivity}
                            onChange={e => setSensitivity(e.target.value)}
                            disabled={phase !== STATUS.WAITING}
                        />
                        <small className="AdmRunToGoal__Help text-small" style={{display:'block', marginBottom:'15px'}}>Menor a 1 = más difícil. 1 = Normal. Mayor a 1 = más fácil</small>
                    </div>

                    {/* BOTONERA TÁCTICA */}
                    <div className='AdmRunToGoal__SpaceUp' style={{marginTop: '25px', display: 'flex', flexDirection: 'column', gap: '10px'}}>
                        
                        {phase === STATUS.WAITING && (
                            <>
                                {/* <small className='text-center'>Oprimir solo una vez este botón, antes de iniciar la partida. Es para sacar a todos los que pudieran estar conectados y para tema de estadísticas. Al presionarlo, se va a limpiar la tabla de abajo, va a quedar en ceros</small> */}
                                <button 
                                    type='button' 
                                    className={`btn btn-outline-danger fw-bold w-100 ${isWorking ? 'disabled' : ''}`}
                                    onClick={PurgeRoom}
                                    disabled={isWorking}
                                    style={{marginTop: '5px'}}
                                >
                                    Abrir sala y Mostrar el QR
                                </button>

                                {/* <small className='text-center'>Ahora, muestra la portada y envía el permiso a los celulares conectados. Este lo presionas cada X tiempo para que le vaya apareciendo a los nuevos que van entrando. El botón de Mostrar portada y Mensaje para permiso hacen lo mismo, pero los separé para sea más amigable</small> */}
                                <button 
                                    type='button' 
                                    className={`btn btn-info fw-bold w-100 shadow-sm ${isWorking ? 'disabled' : ''}`}
                                    onClick={ShowPortada}
                                    disabled={isWorking}
                                >
                                    <i className='bi bi-display'></i> 1.- Mostrar portada en pantalla
                                </button>

                                <button 
                                    type='button' 
                                    className={`btn btn-outline-info fw-bold w-100 shadow-sm ${isWorking ? 'disabled' : ''}`}
                                    onClick={ShowPortada}
                                    disabled={isWorking}
                                >
                                    2.- Mensaje para solicitar permiso de micro
                                </button>

                                <button 
                                    type='button' 
                                    className={`Btn__Adsl--RedOutlineTransparent AdmRunToGoal__Settings__Controls__Buttons w-100 ${isWorking ? 'disabled' : ''}`}
                                    onClick={StartGame}
                                    disabled={isWorking}
                                    style={{marginTop: '5px'}}
                                >
                                    {isWorking ? 'Iniciando...' : <><i className='bi bi-play-circle-fill'></i> Iniciar Ruidómetro</>}
                                </button>
                            </>
                        )}

                        {phase === STATUS.RUNNING && (
                            <button 
                                type='button' 
                                className='btn btn-warning btn-lg fw-bold w-100 shadow-sm'
                                onClick={StopGame}
                                style={{color: '#fff', background: '#f0ad4e', borderColor: '#f0ad4e'}}
                            >
                                <i className='bi bi-stop-circle-fill'></i> Terminar dinámica y declarar ganador
                            </button>
                        )}

                        {(phase === STATUS.RUNNING || phase === STATUS.FINISHED) && (
                            <>
                                <button 
                                    type='button' 
                                    className='btn btn-outline-secondary fw-bold w-100'
                                    onClick={ResetGame}
                                >
                                    <i className='bi bi-arrow-counterclockwise'></i> Reiniciar dinámica
                                </button>
                            </>
                        )}

                        {/* PURGAR SALA / INICIAR SALA (Disponible siempre) */}
                        {/* <button 
                            type='button' 
                            className={`btn btn-outline-danger fw-bold w-100 ${isWorking ? 'disabled' : ''}`}
                            onClick={PurgeRoom}
                            disabled={isWorking}
                            style={{marginTop: '5px'}}
                        >
                            Iniciar sala para sacar a todos
                        </button>
                        <small className='text-center'>Oprimir una vez, antes de iniciar la dinámica</small> */}
                    </div>

                    {error && (
                        <p className='AdmRunToGoal__Settings__Controls__Error' style={{color: 'red', marginTop: '10px', fontWeight: 'bold'}}>
                            {error}
                        </p>
                    )}

                </div>

                {/* --- MONITOR EN VIVO DEL RUIDÓMETRO (PUNTAJES DE GUEERA) --- */}
                {phase !== STATUS.WAITING && (
                    <div className='mt-5'>
                        <h5 className="fw-bold border-bottom pb-2 text-primary">Progreso de Gritos</h5>
                        {stats.zones.length === 0 ? (
                            <p className="text-muted small">Esperando gritos de la afición...</p>
                        ) : (
                            stats.zones.map((z, index) => (
                                <div key={index} className="mb-3 bg-light p-3 rounded shadow-sm border">
                                    <div className="d-flex justify-content-between align-items-center mb-1">
                                        <span style={{textTransform:'uppercase', fontWeight:'700', fontSize: '0.95rem'}}>{z.zone}</span>
                                        <span className="badge bg-success" style={{fontSize: '0.9rem'}}>{Math.round(z.score)} PTS</span>
                                    </div>
                                    <div style={{height: '10px', background: '#e9ecef', borderRadius: '5px', overflow:'hidden'}}>
                                        <div style={{
                                            height: '100%', 
                                            width: `${z.progressPercent}%`, 
                                            background: z.zone === stats.winner ? '#ffc107' : 'linear-gradient(90deg, #28a745, #17a2b8)',
                                            transition: 'width 0.3s ease'
                                        }}></div>
                                    </div>
                                </div>
                            ))
                        )}
                        
                        {phase === STATUS.FINISHED && stats.winner !== 'none' && (
                            <div className="alert alert-success mt-4 p-4 text-center shadow border-0" style={{ borderRadius: '12px' }}>
                                <h6 className="text-uppercase fw-bold mb-2" style={{ letterSpacing: '2px', color: '#198754' }}>Resultado Final</h6>
                                <h2 className="fw-black mb-0" style={{fontSize: '1.8rem'}}>
                                    {stats.winner === 'tie' ? '🏆 ¡HA HABIDO UN EMPATE!' : `🏆 GANADOR: ${stats.winner.toUpperCase()}`}
                                </h2>
                            </div>
                        )}
                    </div>
                )}

                {/* --- MONITOR EN VIVO DE CONEXIONES DEL ESTADIO (TIPO TIRO A GOL) --- */}
                <div className='mt-5 mb-5'>
                    <h5 className="fw-bold border-bottom pb-2">Monitoreo de Conexiones en Vivo (Estadio)</h5>
                    
                    {stadiumLiveStats ? (
                        <div>
                            <p className="fw-bold text-muted mb-3" style={{fontSize: '0.9rem'}}>
                                Ronda Activa en Estadio: <span className="text-dark">{stadiumLiveStats.roundName || 'Sin nombre'}</span>
                            </p>
                            
                            <div className="row text-center mb-3">
                                <div className="col-6">
                                    <div className="bg-light p-2 rounded shadow-sm border">
                                        <p className="mb-0 text-muted small">Dispositivos Activos</p>
                                        <h3 className="fw-black text-success mb-0" style={{fontSize: '1.6rem'}}>{stadiumLiveStats.totalCurrentlyActive}</h3>
                                    </div>
                                </div>
                                <div className="col-6">
                                    <div className="bg-light p-2 rounded shadow-sm border">
                                        <p className="mb-0 text-muted small">Histórico Total</p>
                                        <h3 className="fw-black text-secondary mb-0" style={{fontSize: '1.6rem'}}>{stadiumLiveStats.totalEverConnected}</h3>
                                    </div>
                                </div>
                            </div>

                            <table className="table table-striped table-hover text-center align-middle border mt-2" style={{fontSize: '0.85rem'}}>
                                <thead className="table-dark">
                                    <tr>
                                        <th style={{textAlign: 'left', paddingLeft: '15px'}}>Zona</th>
                                        <th>Conectados (Activos)</th>
                                        <th>Salidos</th>
                                        <th>Históricos</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {stadiumLiveStats.zoneStats && stadiumLiveStats.zoneStats.length > 0 ? (
                                        stadiumLiveStats.zoneStats.map((z, i) => (
                                            <tr key={i}>
                                                <td className="fw-bold" style={{textAlign: 'left', paddingLeft: '15px', textTransform: 'uppercase'}}>{z.zone}</td>
                                                <td className="text-success fw-bold" style={{fontSize: '0.95rem'}}>{z.currentActive}</td>
                                                <td className="text-danger">{z.totalLeft}</td>
                                                <td className="text-secondary">{z.totalEntered}</td>
                                            </tr>
                                        ))
                                    ) : (
                                        <tr>
                                            <td colSpan="4" className="text-muted py-3">No hay fanáticos registrados en esta ronda aún.</td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    ) : (
                        <p className="text-muted small">Cargando monitoreo de conexiones del estadio...</p>
                    )}
                </div>

            </section>
        </SecondaryBase>
    );
};

export { NoisemeterV2 };
